import { useState, useEffect, useCallback, FC, useMemo, useRef } from "react";
import { CorporateTheme, font, Theme } from "../../utils/themes";
import debounce from "lodash/debounce";
import styled, { keyframes } from "styled-components";
import DateRangeFilter from "./DateRangeFilter";
import Filters from "./Filters";
import ExpandableSearch from "./ExpandableSearch";
import FilterIcon from "../../assets/icons/FilterIcon";
import Actions from "./Actions";
import Pagination from "./Pagination";
import DataTable from "./DataTable";
import { Column, TableConfig } from "../../types/types";
import { DropResult } from "@hello-pangea/dnd";
import AdvanceSearchDropDown from "./AdvanceSearchDropDown";
import ColumnIdentifier from "./ColumnIdentifier";
import ModalWrapper from "../ModelWrapper";
import RolePermissionButton, { ActionButton } from "./RolePermissionButton";
import { useTheme } from "../../middleware/ThemeContext";
import DeleteIcon from "../../assets/icons/DeleteIcon";
import ColumnIdentifierIcon from "../../assets/icons/ColumnIdentifierIcon";
import ExportIcon from "../../assets/icons/ExportIcon";
import MergeIcon from "../../assets/icons/MergeIcon";
import { StyledHr } from "../../features/organizations/OrganizationOverview/OrganizationOverview";
import { StyledHorizontalLine } from "../StyledComponents/Styledhr";
import StyledText from "../StyledComponents/StyledText";
import SelectIcon from "../../assets/icons/SelectIcon";
import ShortcutButton from "../StyledComponents/ShortcutButton";
import CloseIcon from "../../assets/icons/CloseIcon";
import SyncIcon from "../../assets/icons/SyncIcon";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Container = styled('div')(({ theme }) => ({
  background: theme.CorporateTheme.colors.background,
  borderRadius: CorporateTheme.borderRadius,
  padding: CorporateTheme.spacing.md,
  paddingBottom:0,
  position: 'relative',
}));

export const HeaderContainer = styled('div')<{ theme: Theme, isBorder?: boolean }>(({ theme, isBorder }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: CorporateTheme.spacing.md,
  alignItems: 'flex-start',
  borderBottom: isBorder ? '1px solid ' + theme.CorporateTheme.colors.border : 'none',

  '@media (min-width: 768px)': {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export const TitleGroup = styled.div`
  flex: 1;
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${CorporateTheme.spacing.xs};
`;

export const TitleCount = styled('span')(({ theme }) => ({
  fontSize: '20px',
  fontFamily: "Lato, sans-serif",
  color: theme.CorporateTheme.colors.count,
  fontWeight: '400',
}));

export const Title = styled('h1')(({ theme }) => `
  font-size: 20px;
  font-weight: 700;
  color: ${theme.CorporateTheme.colors.primary} !important;
  font-family: ${font.raleway};
  margin: 0;
`);

export const Subtitle = styled('p')(({ theme }) => ({
  fontSize: '14px',
  color: theme.CorporateTheme.colors.textSecondary,
  margin: CorporateTheme.spacing.xs + '0' + CorporateTheme.spacing.sm + '0',
  fontFamily: font.lato,
}));

export const ControlsContainer = styled.div<{ gap?: string }>`
  display: flex;
  gap: ${({ gap }) => gap ? gap : CorporateTheme.spacing.md};
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
  justify-content: flex-end;
  @media (min-width: 768px) {
    width: auto;
  }
`;

export const FilterButton = styled('button')<{ $active?: boolean, disabled?: boolean }>(
  ({ theme, $active, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: CorporateTheme.spacing.sm,
    border: 'none',
    background: $active
      ? theme.CorporateTheme.colors.filterBG
      : 'none',
    padding: `${CorporateTheme.spacing.sm} ${CorporateTheme.spacing.sm}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? '0.5' : '1',
    color: theme.CorporateTheme.colors.textSecondary,
    fontSize: '14px',
    fontFamily: '"Raleway", sans-serif',
    fontWeight: 500,
    transition: 'all 0.2s',
    borderRadius: CorporateTheme.borderRadius,

    '&:hover': {
      color: theme.CorporateTheme.colors.accent,
      background: $active
        ? theme.CorporateTheme.colors.filterBG
        : disabled ?
          'transparent'
          : 'rgba(0, 0, 0, 0.05)',
    },
  })
);

export const SelectButton = styled('button')<{ $active?: boolean }>(({ theme, $active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: CorporateTheme.spacing.sm,
  border: 'none',
  background: $active ? theme.CorporateTheme.colors.filterBG : 'none',
  padding: CorporateTheme.spacing.sm,
  cursor: 'pointer',
  color: theme.CorporateTheme.colors.textSecondary,
  fontSize: '14px',
  fontFamily: '"Raleway", sans-serif',
  fontWeight: 500,
  transition: 'all 0.2s',
  borderRadius: CorporateTheme.borderRadius,

  '&:hover': {
    color: theme.CorporateTheme.colors.accent,
    background: $active
      ? theme.CorporateTheme.colors.filterBG
      : 'rgba(0, 0, 0, 0.05)',
  },
}));

export const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: #dddddd;
  align-self: center;
`;

interface TableProps {
  title: string;
  subtitle: string;
  columns: Column[];
  data: any[];
  totalItems: number;
  loading: boolean;
  isFetching?: boolean;
  isFetchComplete?: boolean;
  noDataTitle?: string;
  noDataSubtitle?: string;
  routeKey: string;
  showAdvanceSearch?: boolean;
  showFilters?: boolean;
  showDateRangeFilter?: boolean;
  showActions?: boolean;
  noTableAction?: boolean;
  onAction: (action: "sync" | "view" | "edit" | "delete" | "share" | "copy" | "duplicate" | "revoke" | "condition" | "ignore" | "showAssignedContacts", id: string) => void;
  onFetchRequested?: (params: {
    page: number;
    perPage: number;
    sort?: { field: string; direction: "ASC" | "DESC" };
    query?: string;
    filters: Record<string, any>;
    visibleColumns: string[];
  }) => void;
  filterGroups?: {
    dataType?: string;
    key: string;
    label: string;
    options: { value: string; label: string }[];
  }[];
  setInitialFilters?: any;
  setSelectedRecordIds?: (ids: number[]) => void;
  dynamicColumnsListing?: boolean;
  skeletonHeight?: string;
  showPagination?: boolean;
  containerHeight?: number;
  isBorder?: boolean;
  hideActionColumn?: boolean;
  dateRangePlaceholder?: string;
  dateRangeClassname?: string;
  showDivider?: boolean;
  controlsContainerGap?: string;
  showSelectionButton?: boolean;
  refetchListing?: () => void;
  defaultFilters?: Record<string, any>,
}

const AMSListing: FC<TableProps> = ({
  title,
  subtitle,
  noDataTitle,
  noDataSubtitle,
  columns,
  data,
  totalItems,
  loading,
  isFetching,
  isFetchComplete,
  onAction,
  onFetchRequested,
  setInitialFilters,
  filterGroups,
  routeKey,
  setSelectedRecordIds,
  showAdvanceSearch,
  showFilters,
  showDateRangeFilter,
  showActions,
  noTableAction,
  dynamicColumnsListing,
  skeletonHeight,
  showPagination = true,
  containerHeight,
  isBorder,
  hideActionColumn,
  dateRangePlaceholder,
  dateRangeClassname,
  showDivider = false,
  controlsContainerGap,
  showSelectionButton,
  refetchListing
  , defaultFilters
}) => {

  const { theme } = useTheme();
  const STORAGE_KEY = `tableConfig_${routeKey}`;
  const [filtersContainerHeight, setFiltersContainerHeight] = useState<number>(0);

  // Memoize default configuration
  const defaultConfig = useMemo(() => ({
    visibleColumns: columns?.map(col => col.id) || [],
    dynamicColumns: [],
    page: 1,
    perPage: 10,
    sort: undefined,
    filters: {},
    query: "",
    columnOrder: columns?.map(col => col.id) || [],
  }), [columns]);

  const [tableConfig, setTableConfig] = useState<TableConfig>(defaultConfig);
  const [searchValue, setSearchValue] = useState(tableConfig.query); // UI input state
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isFiltersOpen, setisFiltersOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [showCheckboxColumn, setShowCheckboxColumn] = useState(false);
  const [isColumnIdentifierModalOpen, setIsColumnIdentifierModalOpen] = useState(false);
  const [hasAdjustedPage, setHasAdjustedPage] = useState(false);

  // 🔹 Add ref to track previous page
  const previousPageRef = useRef<number>(tableConfig.page);

  const hasAppliedFilters = useMemo(() => {
    return Object.values(tableConfig.filters as Record<string, unknown>).some((raw) => {
      const val: any = raw as any;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === 'string') return (val as string).trim() !== '';
      if (typeof val === 'boolean') return true; // treat any boolean as applied
      return val !== undefined && val !== null;
    });
  }, [tableConfig.filters]);

  const totalPages = useMemo(() =>
    Math.ceil(totalItems / tableConfig.perPage),
    [totalItems, tableConfig.perPage]
  );

  const shouldPersistConfig = useCallback(() =>
    ['fields', 'components', 'forms'].includes(routeKey),
    [routeKey]
  );

  // Stable debounced fetch function
  const debouncedFetch = useMemo(() =>
    debounce(
      (config: Parameters<NonNullable<typeof onFetchRequested>>[0]) =>
        (onFetchRequested ?? (() => { }))(config),
      300
    ),
    [onFetchRequested]
  );

  // Initialize config only once when component mounts
  useEffect(() => {
    if (!columns || columns.length === 0) return;

    try {
      let parsedConfig = defaultConfig;
      if (shouldPersistConfig()) {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const savedConfig = JSON.parse(saved);
          if (savedConfig &&
            Array.isArray(savedConfig.visibleColumns) &&
            typeof savedConfig.page === 'number' &&
            typeof savedConfig.perPage === 'number'
          ) {
            parsedConfig = {
              ...defaultConfig,
              visibleColumns: savedConfig.visibleColumns,
              filters: savedConfig.filters || {},
              page: savedConfig.page,
              perPage: savedConfig.perPage
            };
          }
        }
      }

      // If there are no saved filters and a default is provided, apply it
      if (defaultFilters && (!parsedConfig.filters || Object.keys(parsedConfig.filters).length === 0)) {
        parsedConfig = {
          ...parsedConfig,
          filters: { ...defaultFilters },
        };
      }

      setTableConfig(parsedConfig);
    } catch (error) {
      console.error('Error initializing table config:', error);
      setTableConfig(defaultConfig);
    }
    // We only want this to run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to update initial filters when tableConfig changes
  useEffect(() => {
    if (setInitialFilters) {
      setInitialFilters({
        page: tableConfig.page,
        perPage: tableConfig.perPage,
        sort: tableConfig.sort,
        query: tableConfig.query,
        filters: tableConfig.filters,
        visibleColumns: tableConfig.visibleColumns
      });
    }
  }, [tableConfig, setInitialFilters]);

  // Effect to trigger fetches and persist config
  useEffect(() => {
    if (!tableConfig.visibleColumns || tableConfig.visibleColumns.length === 0) return;

    const config = {
      page: tableConfig.page,
      perPage: tableConfig.perPage,
      sort: tableConfig.sort,
      query: tableConfig.query,
      filters: {
        ...tableConfig.filters,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      },
      visibleColumns: tableConfig.visibleColumns,
    };

    if (shouldPersistConfig()) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        visibleColumns: tableConfig.visibleColumns,
        filters: tableConfig.filters,
        page: tableConfig.page,
        perPage: tableConfig.perPage
      }));
    }

    debouncedFetch(config);
  }, [
    tableConfig.page,
    tableConfig.perPage,
    tableConfig.sort,
    tableConfig.query,
    tableConfig.filters,
    tableConfig.visibleColumns,
    startDate,
    endDate,
    debouncedFetch,
    shouldPersistConfig
  ]);

  // Replace your previous "move to previous page dynamically" useEffect with this one
  useEffect(() => {
    if (!data) return;

    // Only adjust once per empty page
    if (data.length === 0 && tableConfig.page > 1 && !hasAdjustedPage) {
      setHasAdjustedPage(true); // mark adjustment done
      setTableConfig(prev => ({
        ...prev,
        page: prev.page - 1,
      }));
    } else if (data.length > 0 && hasAdjustedPage) {
      // reset flag when new data arrives
      setHasAdjustedPage(false);
    }
  }, [data, tableConfig.page, hasAdjustedPage]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const newColumnOrder = Array.from(tableConfig.columnOrder);
      const [removed] = newColumnOrder.splice(result.source.index, 1);
      newColumnOrder.splice(result.destination.index, 0, removed);

      setTableConfig(prev => ({
        ...prev,
        columnOrder: newColumnOrder,
      }));
    },
    [tableConfig.columnOrder]
  );

  const handleSort = useCallback((field: string) => {
    setTableConfig(prev => ({
      ...prev,
      sort: {
        field,
        direction: prev.sort?.direction === "ASC" ? "DESC" : "ASC",
      },
      page: 1
    }));
  }, []);

  const debouncedSetQuery = useMemo(() =>
    debounce((value: string) => {
      setTableConfig(prev => ({
        ...prev,
        query: value,
        page: 1
      }));
    }, 300),
    []
  );

  const handleSelectAll = useCallback((ids: number[]) => {
    setSelectedIds(ids);
    if (setSelectedRecordIds) {
      setSelectedRecordIds(ids);
    }
  }, [setSelectedRecordIds]);

  const handleSelectRow = useCallback((id: number) => {
    setSelectedIds(prev => {
      const newIds = prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id];

      if (setSelectedRecordIds) {
        setSelectedRecordIds(newIds);
      }
      return newIds;
    });
  }, [setSelectedRecordIds]);

  const handleDeleteSelected = useCallback(() => {
    if (setSelectedRecordIds) {
      setSelectedRecordIds(selectedIds);
      onAction?.("delete", "");
    }
    setSelectedIds([]);
  }, [selectedIds, onAction, setSelectedRecordIds]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleToggleColumnIdentifier = useCallback(() => {
    setIsColumnIdentifierModalOpen(true);
  }, []);

  const handleSaveColumns = useCallback((
    dynamicColumns: Column[],
    selectedColumns: string[]
  ) => {
    const newVisibleColumns = selectedColumns?.length > 0 ? selectedColumns : [];
    const newColumnOrder = newVisibleColumns;

    setTableConfig(prev => ({
      ...prev,
      dynamicColumns,
      visibleColumns: newVisibleColumns.filter(col => col !== "id"),
      columnOrder: newColumnOrder,
      page: 1
    }));

    setIsColumnIdentifierModalOpen(false);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSetQuery(value);
  };

  const formatter = new Intl.NumberFormat('en-US');

  return (
    <Container theme={theme} style={{ margin: '12px' }}>
      <HeaderContainer theme={theme} isBorder={isBorder}>
        <TitleGroup>
          <TitleWrapper>
            <Title theme={theme}>{title}</Title>
            {routeKey !== "preview" && <TitleCount theme={theme}>({totalItems ? formatter.format(totalItems) : 0})</TitleCount>}
          </TitleWrapper>
          <Subtitle theme={theme}>{subtitle}</Subtitle>
        </TitleGroup>
        <ControlsContainer gap={controlsContainerGap}>

          {showSelectionButton ? <ShortcutButton
            icon={showCheckboxColumn ? <CloseIcon color={theme.CorporateTheme.colors.headerColor} /> : <SelectIcon color={theme.CorporateTheme.colors.headerColor} />}
            label={showCheckboxColumn ? "Cancel" : "Select"}
            onClick={() => setShowCheckboxColumn(!showCheckboxColumn)}
            height="34px"
            textColor={theme.CorporateTheme.colors.textSecondary}
          /> : null}
          {routeKey === "importsListing" &&
            data.some(item => item.statusId === 56) &&
            <ActionButton theme={theme} onClick={refetchListing}>
              <SyncIcon color={theme.CorporateTheme.colors.textSecondary} /> Refresh
            </ActionButton>
          }
          {showDivider ? <Divider /> : null}
          {routeKey !== "preview" && routeKey !== "contact_login_attempts" && <ExpandableSearch
            disabled={loading || isFetching}
            value={searchValue}
            onChange={handleSearchChange}
          />}
          {title.includes("Contact Roles") && <RolePermissionButton disabled={loading || isFetching || data?.length === 0} />}
          {showAdvanceSearch && <><AdvanceSearchDropDown /><Divider /></>}
          {showDivider ? <Divider /> : null}
          {showDateRangeFilter && (
            <DateRangeFilter
              routeKey={routeKey}
              onDateChange={setDateRange}
              startDate={startDate}
              endDate={endDate}
              placeholder={dateRangePlaceholder}
              hideLabel
              dateRangeClassname={dateRangeClassname}
            />
          )}
          {showDivider ? <Divider /> : null}
          {showFilters && (
            <FilterButton
              disabled={loading || isFetching || data?.length === 0}
              theme={theme}
              onClick={() => { if (!loading || !isFetching || data?.length !== 0) setisFiltersOpen(!isFiltersOpen) }}
              $active={
                isFiltersOpen ||
                hasAppliedFilters ||
                !!localStorage.getItem(`persistedDateRange_${routeKey}`)
              }
            >
              <FilterIcon color={theme.CorporateTheme.colors.headerColor} />
              {!hasAppliedFilters ? <StyledText fontSize="14px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>Filters</StyledText> : <StyledText fontSize="14px" fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>Applied Filters</StyledText>}

              {hasAppliedFilters && (
                <FilterCountBadge>
                  {Object.values(tableConfig.filters as Record<string, unknown>).reduce((count: number, raw: unknown) => {
                    const values: any = raw as any;
                    if (values === undefined || values === null) return count;
                    if (Array.isArray(values) && values.length > 0) {
                      return count + 1; // count whole group once
                    }
                    if (typeof values === "string" && (values as string).trim() !== "") {
                      return count + 1; // count string group
                    }
                    if (typeof values === 'boolean') {
                      return count + 1; // count boolean group
                    }
                    return count;
                  }, 0)}
                </FilterCountBadge>
              )}

            </FilterButton>
          )}
          {showActions && (
            <Actions
              disabled={loading || isFetching || data?.length === 0}
              actions={[
                {
                  type: "deleteSelected",
                  label: "Delete Selected",
                  icon: <DeleteIcon />,
                  onClick: handleDeleteSelected,
                  visible: title?.includes("Form") && showCheckboxColumn,
                },
                {
                  type: "toggleColumnIdentifier",
                  label: "Column Identifier",
                  icon: (
                    <ColumnIdentifierIcon color={theme.CorporateTheme.colors.textSecondary} />
                  ),
                  onClick: handleToggleColumnIdentifier,
                  visible: !!title && !title.includes("Form") && !title.includes("Field"),
                },
                {
                  type: "export",
                  label: "Export Data",
                  icon: <ExportIcon color={theme.CorporateTheme.colors.textSecondary} />,
                  onClick: () => console.log("export"),
                  visible: !["Components", "Fields", "All Forms"].some(word => title?.includes(word)),
                },
                {
                  type: "toggleSelect",
                  label: showCheckboxColumn ? "Hide Selection" : "Show Selection",
                  icon: (
                    <ColumnIdentifierIcon color={theme.CorporateTheme.colors.textSecondary} />
                  ),
                  onClick: () => setShowCheckboxColumn(!showCheckboxColumn),
                  visible: true && !["components"].includes(routeKey),
                },
                {
                  type: "mergeContacts",
                  label: "Merge Contacts",
                  icon: <MergeIcon color={theme.CorporateTheme.colors.textSecondary} />,
                  onClick: () => { },
                  visible: title?.includes("Contacts Listing"),
                },
              ]}
            />
          )}

        </ControlsContainer>
      </HeaderContainer>
      <Filters
        setShowFilters={setisFiltersOpen}
        setFiltersContainerHeight={setFiltersContainerHeight}
        isFiltersOpen={isFiltersOpen}
        filterGroups={filterGroups ?? []}
        filters={tableConfig.filters}
        routeKey={routeKey}
        onFilterChange={(newFilters) => setTableConfig(prev => ({
          ...prev,
          filters: newFilters,
          page: 1,
        }))}
      />
      <StyledHorizontalLine theme={theme} />
      <DataTable
        routeKey={routeKey}
        forms={title.includes("Form")}
        isFields={title.includes("Field")}
        components={title.includes("Component")}
        title={title}
        showCheckboxColumn={showCheckboxColumn}
        columns={columns}
        data={data}
        columnOrder={tableConfig.columnOrder}
        visibleColumns={tableConfig.visibleColumns}
        sort={tableConfig.sort}
        noDataTitle={noDataTitle}
        noDataSubtitle={noDataSubtitle}
        onDragEnd={handleDragEnd}
        onSort={handleSort}
        loading={loading}
        isFetching={isFetching}
        isFetchComplete={isFetchComplete}
        perPage={tableConfig.perPage}
        noAction={noTableAction}
        selectedIds={selectedIds}
        onSelectAll={handleSelectAll}
        onSelectRow={handleSelectRow}
        onAction={onAction}
        dynamicColumnsListing={dynamicColumnsListing}
        skeletonHeight={skeletonHeight}
        containerHeight={containerHeight}
        filterHeight={filtersContainerHeight}
        hideActionColumn={hideActionColumn}
      />

      {!loading && !isFetching && data?.length > 0 && showPagination && (
        <PaginationWrapper theme={theme}>
          <Pagination
            currentPage={tableConfig.page}
            totalItems={totalItems}
            perPage={tableConfig.perPage}
            totalPages={totalPages}
            onPageChange={(page) => setTableConfig(prev => ({ ...prev, page }))}
            onLimitChange={(newLimit) => setTableConfig(prev => ({
              ...prev,
              perPage: newLimit,
              page: 1,
            }))}
          />
        </PaginationWrapper>)
      }

      {isColumnIdentifierModalOpen && (
        <ModalWrapper onClose={() => setIsColumnIdentifierModalOpen(false)}>
          <ColumnIdentifier
            entityType={routeKey}
            onClose={() => setIsColumnIdentifierModalOpen(false)}
            onSave={handleSaveColumns}
            availableColumns={[]}
            initialSelectedColumns={tableConfig.visibleColumns || []}
          />
        </ModalWrapper>
      )}
    </Container>
  );
};

export default AMSListing;

export const FilterCountBadge = styled.span`
  background-color: #0D2349; 
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px; /* fully rounded */
  padding: 2px 8px;
  margin-left: 8px; /* space from text */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
`;
export const PaginationWrapper = styled.div`
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border-top: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  text-align: right;
  z-index: 5; /* keeps above table rows */
`;